import { createRouter, createWebHistory } from "vue-router";
import { getItem } from "@/utils/storage.js";
const OnboardingView = () => import("@/views/OnboardingView.vue");
const DashboardView = () => import("@/views/DashboardView.vue");

const checkPermission = (permission) => {
  const teamMember = getItem('team_member');

  if (!teamMember) {
    return true;
  }

  try {
    const permissions = teamMember.groups[0]?.permissions[0];

    let userActions = [];
    if (permissions && permissions?.actions) {
      userActions = permissions?.actions;
    }

    if (permission.endsWith('*')) {
      const prefix = permission.slice(0, -1);
      return userActions.some(action => action.startsWith(prefix));
    }

    return userActions.includes(permission);
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
}


const isPinRequired = (details, team_member) => {
  const hasPinSet = (entity) => entity && entity.pin_set === true;

  // For team members, check team member PIN status
  if (team_member) {
    return !hasPinSet(team_member);
  }

  // For organization owners/admins, check organization PIN status
  return !hasPinSet(details);
};

const checkLoginState = (to, from, next) => {
  try {
    document.title = to.meta.title || "Company Dealroom";

    const details = getItem("org_details");
    const team_member = getItem("team_member");
    const two_fa = getItem("two_fa_verified");

    if (!details) {
      return next({ name: "OnboardingView" });
    }

    if (!details.verified) {
      return next({
        name: "CodeVerification", params: { type: details.type },
      });
    }

    if (isPinRequired(details, team_member)) {
      return next({ name: "CreatePin" });
    }

    if (
      to.name === "OverviewDetails" &&
      from.name === "SignIn" &&
      !team_member &&
      details.kyc &&
      Object.values(details.kyc).includes("")
    ) {
      return next({ name: "Setup" });
    }

    const requires2FA =
      (details.two_factor_enabled && details.two_factor_secret && !two_fa) ||
      (team_member?.two_factor_enabled && !two_fa);

    if (requires2FA) {
      return next({ name: "2Fa" });
    }

    if (details.verified || team_member) {
      const requiredPermission = to.meta.requiredPermission;

      if (!requiredPermission || checkPermission(requiredPermission)) {
        return next();
      } else {
        return next('/dashboard/overview');
      }
    }

    return next('/dashboard/overview');

  } catch (error) {
    console.error("Navigation guard error:", error);
    return next('/error');
  }
};

const checkToken = (to, from, next) => {
  document.title = to.meta.title || "Company Dealroom";

  const details = getItem("org_details");
  const team_member = getItem("team_member");

  if (!details) return next();
  if (!details.verified) return next();
  if (details.founders && details.founders.length === 0) return next();
  if (!details.kyc?.identityDocument) return next();
  if (!details.kyc?.legalDocument) return next();

  if (isPinRequired(details, team_member)) return next();

  const requires2FA =
    (details.two_factor_enabled && details.two_factor_secret) ||
    team_member?.two_factor_enabled;

  if (requires2FA) return next();

  if (details.verified) return next("/dashboard");

  next();
};

const hasToken = (to, from, next) => {
  to.meta.title
    ? (document.title = to.meta.title)
    : (document.title = "Company Dealroom");
  const token = getItem("org_token");
  if (!token) return next({ name: "SignIn" });
  return next();
};

const updatePageTitle = (to, from, next) => {
  to.meta.title
    ? (document.title = to.meta.title)
    : (document.title = "Company Dealroom");

  next();
};

const checkKyc = (to, from, next) => {
  to.meta.title
    ? (document.title = to.meta.title)
    : (document.title = "Company Dealroom");
  const details = getItem("org_details");
  if (
    details.kyc.identityDocument !== "Approved" ||
    details.kyc.legalDocument !== "Approved"
  ) {
    return next({ name: "Asset", params: { type: to.params.type } });
  }

  next();
};

const checkKycStatus = (to, from, next) => {
  to.meta.title
    ? (document.title = to.meta.title)
    : (document.title = "Company Dealroom");
  const details = getItem("org_details");
  if (
    details.kyc.identityDocument === "Approved" &&
    details.kyc.legalDocument === "Approved"
  ) {
    return next({ name: "CreateAsset", params: { type: to.params.type } });
  }
  next();
};

const routes = [
  {
    path: "/logout",
    name: "Logout",
    // beforeEnter: checkToken,
    component: () =>
      import(
        /* webpackChunkName: "logout" */
        "@/components/general/Logout.vue"
      ),
  },
  {
    path: "",
    name: "GetStarted",
    redirect: { name: "CreateAccount" },
    beforeEnter: updatePageTitle,
    meta: { title: "Get Started" },
    // component: () =>
    //   import(
    //     /* webpackChunkName: "onboarding" */
    //     "@/layout/onboarding/GetStarted.vue"
    //   ),
  },
  {
    path: "/setup",
    name: "Setup",
    beforeEnter: hasToken,
    meta: { title: "Setup" },
    component: () =>
      import(
        /* webpackChunkName: "onboarding" */
        "@/layout/setup/Index.vue"
      ),
  },
  {
    path: "/faqs",
    name: "FAQ",
    beforeEnter: updatePageTitle,
    meta: { title: "Faqs" },
    component: () =>
      import(
        /* webpackChunkName: "overviewDetails" */
        "@/layout/landing/Faqs.vue"
      ),
  },
  {
    path: "/onboarding",
    name: "OnboardingView",
    component: OnboardingView,
    redirect: { name: "SignIn" },
    children: [
      { path: "", redirect: "/onboarding/signin" },
      {
        path: "signin",
        name: "SignIn",
        beforeEnter: checkToken,
        meta: { title: "Sign In" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/SignIn.vue"
          ),
      },
      {
        path: "account-type",
        name: "CreateAccount",
        beforeEnter: checkToken,
        meta: { title: "Account Type" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/AccountType.vue"
          ),
      },
      {
        path: "code-verification/:type",
        name: "CodeVerification",
        beforeEnter: checkToken,
        meta: { title: "Verification" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/CodeVerification.vue"
          ),
      },
      {
        path: "add-bank",
        name: "AddBank",
        beforeEnter: hasToken,
        meta: { title: "Add Bank" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/AddBank.vue"
          ),
      },
      {
        path: "create-pin",
        name: "CreatePin",
        beforeEnter: checkToken,
        meta: { title: "Create Pin" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/CreatePin.vue"
          ),
      },
      {
        path: "confirm-pin",
        name: "ConfirmPin",
        beforeEnter: checkToken,
        meta: { title: "Confirm Pin" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/ConfirmPin.vue"
          ),
      },
      {
        path: "/2fa-verification",
        name: "2Fa",
        beforeEnter: checkToken,
        meta: { title: "2Fa Verification" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/signup/2Fa.vue"
          ),
      },
      {
        path: "contact-person",
        name: "ContactPerson",
        beforeEnter: checkToken,
        meta: { title: "Contact Person" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/kyc/ContactPerson.vue"
          ),
      },
      {
        path: "data-collection/:type",
        name: "DataCollection",
        beforeEnter: updatePageTitle,
        meta: { title: "Data Collection" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/createAccount/DataCollection.vue"
          ),
      },
      {
        path: "request-link",
        name: "RequestLink",
        beforeEnter: checkToken,
        meta: { title: "Request Link" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/resetPassword/RequestLink.vue"
          ),
      },
      {
        path: "reset-password",
        name: "ResetPassword",
        beforeEnter: checkToken,
        meta: { title: "Reset Password" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/resetPassword/ResetPassword.vue"
          ),
      },
      {
        path: "upload-id",
        name: "UploadId",
        beforeEnter: checkToken,
        meta: { title: "ID Upload" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/kyc/UploadId.vue"
          ),
      },
      {
        path: "upload-document",
        name: "UploadDocument",
        beforeEnter: checkToken,
        meta: { title: "Upload Document" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/kyc/UploadDocument.vue"
          ),
      },
      {
        path: "selfie",
        name: "UploadSelfie",
        beforeEnter: checkToken,
        meta: { title: "Selfie Upload" },
        component: () =>
          import(
            /* webpackChunkName: "onboarding" */
            "@/layout/onboarding/kyc/UploadSelfie.vue"
          ),
      },
    ],
  },
  {
    path: "/dashboard",
    name: "DashboardView",
    component: DashboardView,
    children: [
      { path: "", redirect: { name: "OverviewDetails" } },
      {
        path: "features",
        name: "Features",
        beforeEnter: checkLoginState,
        meta: { title: "Features" },
        component: () =>
          import(
            /* webpackChunkName: "overviewDetails" */
            "@/layout/overview/FeaturesList.vue"
          ),
      },
      {
        path: "overview",
        name: "OverviewDetails",
        beforeEnter: checkLoginState,
        meta: { title: "Overview" },
        component: () =>
          import(
            /* webpackChunkName: "overviewDetails" */
            "@/layout/overview/OverviewDetails.vue"
          ),
      },
      {
        path: "/dashboard/public-raise",
        name: "PublicRaise",
        beforeEnter: checkLoginState,
        meta: { title: "Public Raise", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "publicRaise" */
            "@/layout/publicRaise/PublicRaiseIndex.vue"
          ),
      },
      {
        path: "/dashboard/public-raise/create",
        name: "CreatePublicRaise",
        beforeEnter: checkLoginState,
        meta: { title: "Create Token", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "publicRaise" */
            "@/layout/publicRaise/CreatePublicRaise.vue"
          ),
      },
      {
        path: "employee-benefits",
        name: "EmployeeBenefits",
        beforeEnter: updatePageTitle,
        meta: { title: "Employee Benefits", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/EmployeeBenefitIndex.vue"
          ),
      },
      {
        path: "employee-benefits/details",
        name: "EsopDetails",
        beforeEnter: updatePageTitle,
        meta: { title: "Employee Benefits", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/EsopDetails.vue"
          ),
      },
      {
        path: "employee-benefits/create",
        name: "CreateStockOptions",
        beforeEnter: updatePageTitle,
        meta: { title: "Employee Benefits", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/CreateStockOptions.vue"
          ),
      },
      {
        path: "employee-benefits/invite-employee",
        name: "InviteEmployee",
        beforeEnter: updatePageTitle,
        meta: { title: "Invite Employee", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/InviteEmployee.vue"
          ),
      },
      {
        path: "employee-benefits/employees",
        name: "Employees",
        beforeEnter: updatePageTitle,
        meta: { title: "Employees", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/Employees.vue"
          ),
      },
      {
        path: "employee-benefits/stock-trade",
        name: "StockTrade",
        beforeEnter: updatePageTitle,
        meta: { title: "Stock Trade", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/StockTrade.vue"
          ),
      },
      {
        path: "employee-benefits/transactions",
        name: "EsopTransactions",
        beforeEnter: updatePageTitle,
        meta: { title: "Stock Transactions", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "employeeBenefits" */
            "@/layout/employeesBenefit/EsopTransactions.vue"
          ),
      },
      {
        path: "/dashboard/credit-provider",
        component: () => import("@/views/Main.vue"),
        children: [
          {
            path: "",
            name: "CreditProvider",
            beforeEnter: updatePageTitle,
            meta: { title: "Credit Provider Dashboard", requiredPermission: "credit:*" },
            component: () =>
              import("@/layout/creditProvider/dashboard.vue"),
          },
          {
            path: "loan-configuration",
            name: "LoanConfiguration",
            beforeEnter: updatePageTitle,
            meta: { title: "Loan Configuration", requiredPermission: "credit:*" },
            component: () =>
              import("@/layout/creditProvider/loanConfiguration.vue"),
          },
          {
            path: "borrower/:id",
            name: "CreditBorrowers",
            beforeEnter: updatePageTitle,
            meta: { title: "Credit Provider Borrowers", requiredPermission: "credit:*" },
            component: () =>
              import("@/layout/creditProvider/borrower.vue"),
          },
          {
            path: "loans",
            name: "CreditLoans",
            beforeEnter: updatePageTitle,
            meta: { title: "All Loans", requiredPermission: "credit:*" },
            component: () =>
              import("@/layout/creditProvider/AllLoans.vue"),
          },
          {
            path: "borrowers",
            name: "CreditBorrowersList",
            beforeEnter: updatePageTitle,
            meta: { title: "All Borrowers", requiredPermission: "credit:*" },
            component: () =>
              import("@/layout/creditProvider/AllBorrowers.vue"),
          },
        ],
      },
      {
        path: "asset/:type",
        name: "Asset",
        beforeEnter: checkKycStatus,
        meta: { title: "Asset" },
        component: () =>
          import(
            /* webpackChunkName: "asset" */
            "@/layout/asset/Asset.vue"
          ),
      },
      {
        path: "create-asset/:type",
        name: "CreateAsset",
        beforeEnter: checkKyc,
        meta: { title: "Create Asset" },
        component: () =>
          import(
            /* webpackChunkName: "asset" */
            "@/layout/asset/CreateAsset.vue"
          ),
      },
      {
        path: "create-asset-info/:type",
        name: "CreateAssetInfo",
        beforeEnter: updatePageTitle,
        meta: { title: "Asset Info" },
        component: () =>
          import(
            /* webpackChunkName: "asset" */
            "@/layout/asset/CreateAssetInfo.vue"
          ),
      },
      {
        path: "api-client",
        name: "ApiClient",
        beforeEnter: updatePageTitle,
        meta: { title: "API Client", requiredPermission: "api:*" },
        component: () =>
          import(
            /* webpackChunkName: "apiClient" */
            "@/layout/apiClient/Index.vue"
          ),
      },
      {
        path: "api-client/customers",
        name: "ApiClientCustomers",
        beforeEnter: updatePageTitle,
        meta: { title: "API Client Customers", requiredPermission: "api:*" },
        component: () =>
          import(
            /* webpackChunkName: "apiClient" */
            "@/layout/apiClient/customers/AllCustomers.vue"
          ),
      },
      {
        path: "api-client/members",
        name: "ApiClientCustomers",
        beforeEnter: updatePageTitle,
        meta: { title: "API Client Customers", requiredPermission: "api:*" },
        component: () =>
          import(
            /* webpackChunkName: "apiClient" */
            "@/layout/apiClient/customers/AllCustomers.vue"
          ),
      },
      {
        path: "api-client/customers/:email",
        name: "ApiClientCustomerTransaction",
        beforeEnter: updatePageTitle,
        meta: { title: "API Client Transaction", requiredPermission: "api:*" },
        component: () =>
          import(
            /* webpackChunkName: "apiClient" */
            "@/layout/apiClient/customers/CustomerTransactions.vue"
          ),
      },
      {
        path: "api-client/transactions",
        name: "ApiClientTransactions",
        beforeEnter: updatePageTitle,
        meta: { title: "API Client Transaction", requiredPermission: "api:*" },
        component: () =>
          import(
            /* webpackChunkName: "apiClient" */
            "@/layout/apiClient/AllTransactions.vue"
          ),
      },
      {
        path: "/dashboard/private-raise",
        name: "PrivateRaise",
        beforeEnter: checkLoginState,
        meta: { title: "Private Raise", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/Index.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/request",
        name: "PrivateRaiseRequest",
        beforeEnter: checkLoginState,
        meta: { title: "Private Raise Request", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/DealRequest.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/deals",
        name: "PrivateRaiseDeals",
        beforeEnter: checkLoginState,
        meta: { title: "Private Raise", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/AllDeals.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/members",
        name: "PrivateRaiseMembers",
        beforeEnter: checkLoginState,
        meta: { title: "Members", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/Members.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/:id",
        name: "PrivateRaiseInfo",
        beforeEnter: checkLoginState,
        meta: { title: "Private Raise Info", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/PrivateRaiseOverview.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/:id/configure",
        name: "ConfigurePrivateRaise",
        beforeEnter: checkLoginState,
        meta: { title: "Configuration", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/ConfigureDeal.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/:id/create-spv",
        name: "CreateNewSpv",
        beforeEnter: checkLoginState,
        meta: { title: "Create SPV", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/spv/CreateNewSpv.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/request/:id/create-spv/:country/:spvId",
        name: "AddSpvInfo",
        beforeEnter: checkLoginState,
        meta: { title: "SPV Info", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/spv/Index.vue"
          ),
      },
      {
        path: "/dashboard/private-raise/request/:id/upload-spv",
        name: "SpvUpload",
        beforeEnter: checkLoginState,
        meta: { title: "SPV Upload", requiredPermission: "deal:*" },
        component: () =>
          import(
            /* webpackChunkName: "privateRaise" */
            "@/layout/privateRaise/spv/SpvUpload.vue"
          ),
      },
      {
        path: "/dashboard/wallet",
        name: "Wallet",
        beforeEnter: updatePageTitle,
        meta: { title: "Wallet", requiredPermission: "wallet:*" },
        component: () =>
          import(/* webpackChunkName: "wallet" */ "@/layout/wallet/Wallet.vue"),
      },
      {
        path: "/dashboard/teams",
        name: "Teams",
        beforeEnter: checkLoginState,
        meta: { title: "Teams", requiredPermission: "team:*" },
        component: () =>
          import(
            /* webpackChunkName: "teams" */
            "@/layout/teams/Teams.vue"
          ),
      },
      {
        path: "/dashboard/teams/create-role",
        name: "CreateRoles",
        beforeEnter: checkLoginState,
        meta: { title: "CreateRoles", requiredPermission: "team:*" },
        component: () =>
          import(
            /* webpackChunkName: "create roles" */
            "@/layout/teams/CreateRole.vue"
          ),
      },
      {
        path: "/dashboard/teams/edit-role/:id",
        name: "EditRoles",
        beforeEnter: checkLoginState,
        meta: { title: "EditRoles", requiredPermission: "team:*" },
        component: () =>
          import(
            /* webpackChunkName: "edit roles" */
            "@/layout/teams/EditRole.vue"
          ),
      },
      {
        path: "/dashboard/account",
        name: "Account",
        redirect: { name: "CompanyInfo" },
        beforeEnter: checkLoginState,
        component: () =>
          import(
            /* webpackChunkName: "account" */
            "@/layout/account/Account.vue"
          ),
        children: [
          {
            path: "company-info",
            name: "CompanyInfo",
            beforeEnter: updatePageTitle,
            meta: { title: "Company Info" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/CompanyInfo.vue"
              ),
          },
          {
            path: "verification",
            name: "IdentityVerification",
            beforeEnter: updatePageTitle,
            meta: { title: "Verification" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/IdentityVerification.vue"
              ),
          },
          {
            path: "profile-summary",
            name: "ProfileSummary",
            beforeEnter: updatePageTitle,
            meta: { title: "Profile Summary" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/ProfileSummary.vue"
              ),
          },
          {
            path: "features",
            name: "ManageFeatures",
            beforeEnter: updatePageTitle,
            meta: { title: "Features" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/ManageFeatures.vue"
              ),
          },
          {
            path: "bank",
            name: "ManageBank",
            beforeEnter: updatePageTitle,
            meta: { title: "Manage Bank" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/ManageBank.vue"
              ),
          },
          {
            path: "founders",
            name: "FounderInfo",
            beforeEnter: updatePageTitle,
            meta: { title: "Founders Info" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/FounderInfo.vue"
              ),
          },
          {
            path: "company-updates",
            name: "CompanyUpdate",
            beforeEnter: updatePageTitle,
            meta: { title: "Company Update" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/CompanyUpdate.vue"
              ),
          },
          {
            path: "security",
            name: "Security",
            beforeEnter: updatePageTitle,
            meta: { title: "Security" },
            component: () =>
              import(
                /* webpackChunkName: "account" */
                "@/layout/account/Security.vue"
              ),
          },
        ],
      },
      {
        path: "/new-password",
        name: "ChangePassword",
        beforeEnter: updatePageTitle,
        meta: { title: "Change Password" },
        component: () =>
          import(
            /* webpackChunkName: "ChangePassword" */
            "@/layout/account/ChangePassword.vue"
          ),
      },
      {
        path: "/dashboard/invest",
        name: "Invest",
        beforeEnter: checkLoginState,
        meta: { title: "Invest", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "invest" */
            "@/layout/invest/Invest.vue"
          ),
      },
      {
        path: "/dashboard/trade",
        name: "Trade",
        beforeEnter: checkLoginState,
        meta: { title: "Trade", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/trade/Trade.vue"
          ),
      },
      {
        path: "/dashboard/invest/orderbook",
        name: "InvestOrderbook",
        beforeEnter: checkLoginState,
        meta: { title: "Orderbook", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/OrderBook.vue"
          ),
      },
      {
        path: "/dashboard/invest/opportunities",
        name: "Opportunities",
        beforeEnter: checkLoginState,
        meta: { title: "Opportunities", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/Opportunities.vue"
          ),
      },
      {
        // Sits above the /dashboard/invest/:symbol/:id route on purpose: a single extra
        // segment cannot match a two-segment pattern, but keeping the literals first makes
        // that independent of matcher order.
        path: "/dashboard/invest/investments",
        name: "MyInvestments",
        beforeEnter: checkLoginState,
        meta: { title: "My Investments", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/MyInvestments.vue"
          ),
      },
      {
        path: "/dashboard/invest/exchange",
        name: "Exchange",
        beforeEnter: checkLoginState,
        meta: { title: "Exchange", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/Exchange.vue"
          ),
      },
      {
        // Same component as TokenOverview below, deliberately.
        //
        // These were two ~410-line deal pages that had drifted apart: different metric cards,
        // different progress markup, and — the reason this had to be resolved rather than
        // papered over — they read the token from DIFFERENT store modules (this one from
        // assetModule, the other from TokenModule). Bidding cannot be added to both without
        // maintaining two data paths for the same action, and only one of them was gated on
        // the invest:trade permission.
        //
        // The name is kept because components/tables/wallet/TokensTable.vue routes holdings
        // here; changing it would break that link.
        path: "/dashboard/trade/:symbol/:id",
        name: "TokenTrade",
        beforeEnter: checkLoginState,
        meta: { title: "Trade", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/TokenTrade.vue"
          ),
      },
      {
        path: "/dashboard/invest/:symbol/:id",
        name: "TokenOverview",
        beforeEnter: checkLoginState,
        meta: { title: "Invest", requiredPermission: "invest:*" },
        component: () =>
          import(
            /* webpackChunkName: "dashboard" */
            "@/layout/invest/TokenTrade.vue"
          ),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

export default router;
